/**
 * RequestMoreInfoOrReplyModal
 *
 * Single-step modal that handles two related flows depending on
 * `mir_pending_direction`:
 *
 *  - null / undefined  → "Request more info" (outbound to subcontractor)
 *  - "inbound"         → "Reply to info request" (replying to subcontractor)
 *
 * The caller passes the job, open/submit/submitting state and the
 * modal handles comment + optional file attachments.
 */

import {
  type JobCategoryPrefillText,
  getJobCategoryPrefillTexts,
} from '@/domains/jobs/api/jobTypesApi';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, File as FileIcon, HelpCircle, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export interface RequestMoreInfoOrReplyJob {
  id: number;
  name: string | null;
  address: string | null;
  work_order_status: string | null;
  req_completion_date: string | null;
  mir_pending_direction: 'inbound' | 'outbound' | null | undefined;
}

interface RequestMoreInfoOrReplyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: RequestMoreInfoOrReplyJob | null;
  isSubmitting: boolean;
  onSubmit: (comment: string, files: File[]) => void | Promise<void>;
}

const formSchema = z.object({
  comments: z.string().trim().min(1, 'Required').max(500, 'Maximum 500 characters'),
});

type FormValues = z.infer<typeof formSchema>;

export function RequestMoreInfoOrReplyModal({
  open,
  onOpenChange,
  job,
  isSubmitting,
  onSubmit,
}: RequestMoreInfoOrReplyModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [jobCategories, setJobCategories] = useState<JobCategoryPrefillText[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const selectedCategory = jobCategories.find((cat) => String(cat.id) === selectedCategoryId);

  const isReply = job?.mir_pending_direction === 'inbound';

  useEffect(() => {
    getJobCategoryPrefillTexts()
      .then(setJobCategories)
      .catch(() => setJobCategories([]));
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { comments: '' },
  });

  const commentsValue = watch('comments');
  const hasAnyInput =
    (commentsValue?.trim().length ?? 0) > 0 || files.length > 0 || selectedCategoryId !== '';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (isSubmitting) return;
      if (hasAnyInput) {
        setShowCancelConfirm(true);
      } else {
        reset();
        setFiles([]);
        onOpenChange(false);
      }
    } else {
      onOpenChange(true);
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    reset();
    setFiles([]);
    setSelectedCategoryId('');
    onOpenChange(false);
  };

  const onFormSubmit = handleSubmit(async (values) => {
    await onSubmit(values.comments.trim(), files);
    reset();
    setFiles([]);
    setSelectedCategoryId('');
  });

  /** Append files, de-duping by name + size. */
  const appendFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    appendFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    appendFiles(Array.from(e.dataTransfer.files ?? []));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  if (!job) return null;

  const jobMeta = [
    job.work_order_status,
    job.address,
    job.req_completion_date
      ? `Due ${new Date(job.req_completion_date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0"
          onInteractOutside={(e) => {
            if (isSubmitting) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isSubmitting) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <HelpCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle>{isReply ? 'Reply to info request' : 'Request more info'}</DialogTitle>
                <DialogDescription className="mt-1 truncate">
                  {job.name ?? `Job #${job.id}`}
                  {jobMeta ? ` · ${jobMeta}` : ''}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={onFormSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-5 space-y-5">
              {/* Job category */}
              <div className="space-y-2">
                <Label htmlFor="rmi-category">
                  Job category <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                  <SelectTrigger id="rmi-category">
                    <SelectValue placeholder="Select a category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobCategories.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedCategory?.prefill_text && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const current = commentsValue?.trim() ?? '';
                      setValue(
                        'comments',
                        current
                          ? `${current}\n\n${selectedCategory.prefill_text}`
                          : selectedCategory.prefill_text,
                        { shouldValidate: true },
                      );
                    }}
                  >
                    Add prefill note
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="rmi-comments">
                  {isReply ? 'Your reply' : 'Information request'}{' '}
                  <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="rmi-comments"
                  {...register('comments')}
                  placeholder={
                    isReply
                      ? 'Provide the requested information…'
                      : 'What additional information do you need?'
                  }
                  rows={6}
                  className="resize-none"
                  maxLength={500}
                  autoFocus
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-red-600">{errors.comments?.message ?? ''}</span>
                  <span className="text-muted-foreground">{commentsValue?.length ?? 0} / 500</span>
                </div>
              </div>

              {/* Supporting documentation */}
              <div className="space-y-2">
                <Label>Supporting documentation (optional)</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? 'border-[#E84A2B] bg-orange-50 dark:bg-orange-950/20'
                      : 'border-border hover:border-[#E84A2B] hover:bg-muted/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-foreground">
                      Drop files here or{' '}
                      <label className="text-[#E84A2B] hover:underline cursor-pointer">
                        browse
                        <input
                          type="file"
                          className="hidden"
                          multiple
                          onChange={handleFileInputChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                        />
                      </label>
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, Word, Excel, or images</p>
                  </div>
                </div>

                {files.length > 0 && (
                  <ul className="space-y-2 mt-2">
                    {files.map((file, fileIndex) => (
                      <li
                        key={`${file.name}-${file.size}-${fileIndex}`}
                        className="flex items-center gap-3 p-2 rounded border border-border bg-muted/30"
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                          <FileIcon className="w-4 h-4 text-[#E84A2B]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={() => setFiles((prev) => prev.filter((_, i) => i !== fileIndex))}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </form>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => void onFormSubmit()}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isReply
                  ? 'Sending reply…'
                  : 'Sending…'
                : isReply
                  ? 'Send reply'
                  : 'Send request'}
            </Button>
          </DialogFooter>

          {isSubmitting && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-background/75 backdrop-blur-sm"
              role="status"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
                <p className="text-sm font-medium text-foreground">
                  {isReply ? 'Sending reply…' : 'Sending request…'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Please wait, this can take a moment.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Discard confirm */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle>Discard changes?</DialogTitle>
                <DialogDescription className="mt-1">
                  You have unsaved content. Closing now will lose it.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              Keep editing
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmCancel}>
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
